import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from 'vue'
import { useTranslate, useTranslation } from '../../../i18n/useTranslation'
import { ConfigElementComponentType } from '../enum/ConfigElementComponentType'
import { DynamicComponent } from '../type/DynamicComponent'
import { KhIcon, KhInputType, Nullable } from '@internal-libraries/kheops-ui-lib'
import { useConfigStore } from '../../../Store/configStore'
import { ClvrSkeletonLoaderTypeConst } from '../../../../../prescriptionClevEHR/vue/constants'
import {
    ConfigurationElementDataListPropertyDto,
    ConfigurationElementEnumPropertyDto,
    ConfigurationElementObjectListPropertyDto,
} from '../type/dto/ConfigurationElementDto'

export interface ClvrConfigElementProps {
    value?: unknown
    id: string
    context: string
    label: string
    description: string
    component: ConfigElementComponentType
    componentProps?: Record<string, unknown>
    globalValue: unknown
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ClvrConfigElement',
  props: {
    value: null,
    id: null,
    context: null,
    label: null,
    description: null,
    component: null,
    componentProps: null,
    globalValue: null
  },
  emits: ["input"],
  setup(__props: any, { emit: emits }: { emit: ({
    (e: 'input', value: unknown): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value?: unknown, id: string, context: string, label: string, description: string, component: ConfigElementComponentType, componentProps?: Record<string, unknown>, globalValue: unknown }




const configStore = useConfigStore()
const $t = useTranslate()
const translation = useTranslation()
const translationloading = ref(true)

const sameAsGlobal = computed<boolean>(() => {
    return props.globalValue === customComponentValue.value
})

const labelTr = computed<string>(() => $t.value(props.context + ':' + props.label))
const descriptionTr = computed<string>(() => $t.value(props.context + ':' + props.description))

onMounted(() => {
    translation.i18next.loadNamespaces(['system']).then(() => {
        translationloading.value = false
    })
})

const ConfigComponentProps = computed(() => {
    return {
        [ConfigElementComponentType.String]: {
            name: 'KhInput',
            props: {
                inputType: KhInputType.TextField,
                dense: true,
            },
        },
        [ConfigElementComponentType.Text]: {
            name: 'KhInput',
            props: {
                inputType: KhInputType.TextField,
                dense: true,
            },
        },
        [ConfigElementComponentType.Checkbox]: {
            name: 'KhSwitch',
            props: {
                dense: true,
                inset: true,
            },
        },
        [ConfigElementComponentType.QuantityPicker]: {
            name: 'KhQuantityPicker',
            props: {
                dense: true,
                allowEmpty: true,
                step: 1,
            },
        },
        [ConfigElementComponentType.Textarea]: {
            name: 'KhInput',
            props: {
                inputType: KhInputType.TextArea,
            },
        },
        [ConfigElementComponentType.Dropdown]: {
            name: 'KhSelect',
            props: {
                items: (props.componentProps as ConfigurationElementEnumPropertyDto)?.list || [],
                clearable: true,
            },
        },
        [ConfigElementComponentType.Select]: {
            name: 'KhSelect',
            props: {
                items: (props.componentProps as ConfigurationElementDataListPropertyDto)?.list || [],
                clearable: true,
                itemValue: 'id',
                itemText: 'name',
            },
        },
        [ConfigElementComponentType.Datatable]: {
            name: 'ClvrConfigElementList',
            props: {
                configKey: props.id,
            },
        },
        [ConfigElementComponentType.ObjectSelect]: {
            name: 'ClvrConfigElementObjectSelect',
            props: {
                className: props.componentProps?.className || '',
                listKeyName: props.componentProps?.listKeyName || '',
            },
        },
    }
})

const mappedComponent = computed<Nullable<DynamicComponent>>(() => {
    return ConfigComponentProps.value[props.component] ?? null
})

const customComponentValue = ref<unknown>(undefined)

watch(
    () => props.value,
    newValue => {
        switch (props.component) {
            case ConfigElementComponentType.Checkbox:
                customComponentValue.value = !!newValue
                break
            default:
                customComponentValue.value = newValue
        }
    },
    { immediate: true },
)

watch(customComponentValue, async newValue => {
    emits('input', newValue)
})

function openTranslationEdition(configkey: string) {
    const url = new Url('system', 'view_translations_config')
    url.addParam('feature', configkey)
    url.requestModal('50%', null, {
        onClose: () => {
            translationloading.value = true
            translation.i18next.reloadResources(null, props.context).then(() => {
                translationloading.value = false
            })
        },
    })
}

function openHistory(configkey: string) {
    const feature = configkey.replace('config-', '').replace(/-/g, ' ')
    const url = new Url('system', 'view_config_history')
    url.addParam('feature', feature)
    url.addParam('object_class', 'CGroups')
    url.addParam('object_id', configStore.currentEstablishment)
    url.pop(800, 600)
}

return { __sfc: true,props, emits, configStore, $t, translation, translationloading, sameAsGlobal, labelTr, descriptionTr, ConfigComponentProps, mappedComponent, customComponentValue, openTranslationEdition, openHistory, KhIcon, ClvrSkeletonLoaderTypeConst }
}

})